import { hooks, oauthHelpers } from '@approvalmax/utils';
import * as Sentry from '@sentry/browser';
import { profileSetupSsoState } from 'app/(workspace)/profileSetup/sso';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { helpers as pageHelpers } from 'modules/page';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useRef } from 'react';
import { useInvalidateGetCompanies } from 'shared/data/webApp/v1';
import { useInvalidateGetCompanies as useInvalidateGetCompaniesV2 } from 'shared/data/webApp/v2';
import { useCreateOrganisation } from 'shared/hooks';

import { useLoadData } from './resources/hooks/useLoadData';

const Page = () => {
    const [initialRealmId] = hooks.useQueryParam('realmId', '');
    const [code] = hooks.useQueryParam('code', '');
    const [state] = hooks.useQueryParam('state', '');

    const { uiState, backendState } = oauthHelpers.decodeOAuthState<domain.OAuthUiState>(state);

    const dispatch = useDispatch();
    const company = useSelector((state) =>
        selectors.company.findCompanyById(state, uiState.type === 'oauth' && uiState.companyId ? uiState.companyId : '')
    );

    const realmId = initialRealmId && initialRealmId !== 'null' ? initialRealmId : undefined;
    const firstRenderRef = useRef(true);
    const counterRef = useRef(0);

    const setSsoData = useSetAtom(profileSetupSsoState);

    const previousState = hooks.usePrevious(state);
    const previousCode = hooks.usePrevious(code);
    const previousRealmId = hooks.usePrevious(realmId);
    const invalidateGetCompanies = useInvalidateGetCompanies();
    const invalidateGetCompaniesV2 = useInvalidateGetCompaniesV2();
    const createOrganisation = useCreateOrganisation();

    /**
     * Invalidate company data in react-query cache to get fresh data from the server
     */
    const invalidateCompanyData = useCallback(() => {
        void invalidateGetCompaniesV2();

        if (company) {
            void invalidateGetCompanies({ query: { companyId: company.id } });
        }
    }, [company, invalidateGetCompanies, invalidateGetCompaniesV2]);

    const loadData = useLoadData({
        uiState,
        backendState,
        code,
        company,
        realmId,
        dispatch,
        invalidateCompanyData,
        setSsoData,
        createOrganisation,
    });

    useEffect(() => {
        const noImportantDepsChanges = state === previousState && code === previousCode && realmId === previousRealmId;

        // For some reason, the reference to dependency `company` changes, and this triggers a call to useEffect twice
        if (noImportantDepsChanges) return;

        counterRef.current++;

        if (firstRenderRef.current) {
            firstRenderRef.current = false;

            if (code && state) {
                void loadData();
            } else {
                Sentry.withScope((scope) => {
                    scope.setExtra('code', code);
                    scope.setExtra('state', state);
                    scope.setExtra('uiState', uiState);
                    scope.setExtra('backendState', backendState);
                    scope.setExtra('counter', counterRef.current);

                    Sentry.captureMessage(`oauth error: code and state can't be empty or null. Code - ${code}`);
                });
                pageHelpers.redirectAndNotify();
            }
        } else {
            Sentry.captureMessage("oauth error: code and state can't be empty or null. Second render");
        }
    }, [backendState, code, loadData, previousCode, previousRealmId, previousState, realmId, state, uiState]);

    return null;
};

export default Page;
